import { ChevronUp, PlusCircle, RotateCcw, User } from 'feather-icons-react/build/IconComponents';
import { Button, Spin, notification, Modal } from "antd";
import React, { useEffect, useState } from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { setToogleHeader } from '../../../core/redux/action';
import { CloseOutlined, DeleteOutlined } from '@ant-design/icons';
import { createBusinessSettings, getAllBusinessSettings, updateBusinessSettings, deleteBusinessSettings } from '../../../service/businessSettingsService';
import Swal from 'sweetalert2';

const BusinessSettings = () => {
  const dispatch = useDispatch();
  const data = useSelector((state) => state.toggle_header);
  const [businessProfile, setBusinessProfile] = useState(null);
  const [companyName, setCompanyName] = useState('');
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [branches, setBranches] = useState([{ branchName: '', address: { street: '', city: '', state: '', country: '', postalCode: '' }, contact: { phone: '', email: '' } }]);
  const [contact, setContact] = useState({ phone: '', email: '' });
  const [address, setAddress] = useState({ street: '', city: '', state: '', country: '', postalCode: '' });
  const [salaryPeriod, setSalaryPeriod] = useState({ startDate: '', endDate: '' });
  const [additionalDetails, setAdditionalDetails] = useState({ website: '', industry: '' });
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const renderRefreshTooltip = (props) => (
    <Tooltip id="refresh-tooltip" {...props}>
      Refresh
    </Tooltip>
  );

  const renderCancelTooltip = (props) => (
    <Tooltip id="cancel-tooltip" {...props}>
      Collapse
    </Tooltip>
  );

  useEffect(() => {
    const fetchBusinessProfile = async () => {
      try {
        const response = await getAllBusinessSettings();
        if (response.data && response.data.length > 0) {
          const business = response.data[0];
          setBusinessProfile(business);
          setCompanyName(business.companyName || '');
          setSelectedImage(business.logo || null);
          setImagePreview(business.logo || null);
          const normalizedBranches = (business.branches || []).map(branch => ({
            branchName: branch.branchName || '',
            address: {
              street: branch.address?.street || '',
              city: branch.address?.city || '',
              state: branch.address?.state || '',
              country: branch.address?.country || '',
              postalCode: branch.address?.postalCode || ''
            },
            contact: {
              phone: branch.contact?.phone || '',
              email: branch.contact?.email || ''
            }
          }));
          setBranches(normalizedBranches.length ? normalizedBranches : [{ branchName: '', address: { street: '', city: '', state: '', country: '', postalCode: '' }, contact: { phone: '', email: '' } }]);
          setContact(business.contact || { phone: '', email: '' });
          setAddress(business.address || { street: '', city: '', state: '', country: '', postalCode: '' });
          setSalaryPeriod(business.salaryPeriod || { startDate: '', endDate: '' });
          setAdditionalDetails(business.additionalDetails ? Object.fromEntries(business.additionalDetails) : { website: '', industry: '' });
        } else {
          setBusinessProfile(null);
        }
      } catch (err) {
        notification.error({
          message: 'Error',
          description: 'Failed to fetch business profile',
          placement: 'top'
        });
      }
    };
    fetchBusinessProfile();
  }, []);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    setSelectedImage(file);
    if (file) {
      const previewUrl = URL.createObjectURL(file);
      setImagePreview(previewUrl);
    }
  };

  const handleImageClick = () => {
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
  };

  const handleBranchChange = (index, field, value, subField = '') => {
    const newBranches = [...branches];
    if (subField) {
      newBranches[index][field] = { ...newBranches[index][field], [subField]: value };
    } else {
      newBranches[index][field] = value;
    }
    setBranches(newBranches);
  };

  const addBranch = () => {
    setBranches([...branches, { branchName: '', address: { street: '', city: '', state: '', country: '', postalCode: '' }, contact: { phone: '', email: '' } }]);
  };

  const removeBranch = (index) => {
    setBranches(branches.filter((_, i) => i !== index));
  };

  const handleCancel = () => {
    if (businessProfile) {
      setCompanyName(businessProfile.companyName || '');
      setSelectedImage(businessProfile.logo || null);
      setImagePreview(businessProfile.logo || null);
      const normalizedBranches = (businessProfile.branches || []).map(branch => ({
        branchName: branch.branchName || '',
        address: {
          street: branch.address?.street || '',
          city: branch.address?.city || '',
          state: branch.address?.state || '',
          country: branch.address?.country || '',
          postalCode: branch.address?.postalCode || ''
        },
        contact: {
          phone: branch.contact?.phone || '',
          email: branch.contact?.email || ''
        }
      }));
      setBranches(normalizedBranches.length ? normalizedBranches : [{ branchName: '', address: { street: '', city: '', state: '', country: '', postalCode: '' }, contact: { phone: '', email: '' } }]);
      setContact(businessProfile.contact || { phone: '', email: '' });
      setAddress(businessProfile.address || { street: '', city: '', state: '', country: '', postalCode: '' });
      setSalaryPeriod(businessProfile.salaryPeriod || { startDate: '', endDate: '' });
      setAdditionalDetails(businessProfile.additionalDetails ? Object.fromEntries(businessProfile.additionalDetails) : { website: '', industry: '' });
    } else {
      setCompanyName('');
      setSelectedImage(null);
      setImagePreview(null);
      setBranches([{ branchName: '', address: { street: '', city: '', state: '', country: '', postalCode: '' }, contact: { phone: '', email: '' } }]);
      setContact({ phone: '', email: '' });
      setAddress({ street: '', city: '', state: '', country: '', postalCode: '' });
      setSalaryPeriod({ startDate: '', endDate: '' });
      setAdditionalDetails({ website: '', industry: '' });
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);

    if (!companyName.trim()) {
      notification.error({
        message: 'Validation Error',
        description: 'Company Name is required',
        placement: 'top'
      });
      setLoading(false);
      return;
    }
    if (branches.some(branch => !branch.branchName.trim())) {
      notification.error({
        message: 'Validation Error',
        description: 'All branches must have a Branch Name',
        placement: 'top'
      });
      setLoading(false);
      return;
    }
    if (!salaryPeriod.startDate || !salaryPeriod.endDate) {
      notification.error({
        message: 'Validation Error',
        description: 'Salary Period start and end dates are required',
        placement: 'top'
      });
      setLoading(false);
      return;
    }

    const formDataToSend = new FormData();
    formDataToSend.append('companyName', companyName);
    if (selectedImage && selectedImage instanceof File) {
      formDataToSend.append('logo', selectedImage);
    }
    formDataToSend.append('branches', JSON.stringify(branches));
    formDataToSend.append('contact', JSON.stringify(contact));
    formDataToSend.append('address', JSON.stringify(address));
    formDataToSend.append('salaryPeriod', JSON.stringify(salaryPeriod));
    formDataToSend.append('additionalDetails', JSON.stringify(additionalDetails));

    try {
      for (let [key, value] of formDataToSend.entries()) {
        console.log(`${key}: ${value}`);
      }
      if (businessProfile) {
        const updatedData = await updateBusinessSettings(businessProfile._id, formDataToSend);
        setBusinessProfile(updatedData.data);
        notification.success({
          message: 'Profile Updated',
          description: 'Business profile updated successfully',
          placement: 'top'
        });
      } else {
        const newData = await createBusinessSettings(formDataToSend);
        setBusinessProfile(newData.data);
        notification.success({
          message: 'Profile Created',
          description: 'Business profile created successfully',
          placement: 'top'
        });
      }
    } catch (error) {
      console.error('Error processing business profile:', error);
      notification.error({
        message: 'Operation Failed',
        description: error.response?.data?.error || error.message || 'There was an error processing the business profile',
        placement: 'top'
      });
    }
    setLoading(false);
  };

  const handleDelete = () => {
    if (!businessProfile) return;
    Swal.fire({
      title: 'Are you sure?',
      text: "This action cannot be undone! Deleting the profile will allow creating a new one.",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, delete it',
      cancelButtonText: 'No, keep it'
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await deleteBusinessSettings(businessProfile._id);
          setBusinessProfile(null);
          handleCancel(); 
          Swal.fire(
            'Deleted!',
            'Business profile has been deleted.',
            'success'
          );
        } catch (error) {
          notification.error({
            message: 'Error',
            description: 'Failed to delete business profile',
            placement: 'top'
          });
        }
      }
    });
  };

  return (
    <div>
      <div className="page-wrapper">
        <div className="content settings-content">
          <div className="page-header settings-pg-header">
            <div className="add-item d-flex" style={{display:"none"}}>
              <div className="page-title"  style={{display:"none"}}>
                <h4>Business Settings</h4>
                <h6>Manage your business profile</h6>
              </div>
            </div>
            <ul className="table-top-head"  style={{display:"none"}}>
              <li>
                <OverlayTrigger placement="top" overlay={renderRefreshTooltip}>
                  <a data-bs-toggle="tooltip" data-bs-placement="top">
                    <RotateCcw />
                  </a>
                </OverlayTrigger>
              </li>
              <li>
                <OverlayTrigger placement="top" overlay={renderCancelTooltip}>
                  <a
                    data-bs-toggle="tooltip"
                    data-bs-placement="top"
                    id="collapse-header"
                    className={data ? "active" : ""}
                    onClick={() => dispatch(setToogleHeader(!data))}
                  >
                    <ChevronUp />
                  </a>
                </OverlayTrigger>
              </li>
            </ul>
          </div>
          <div className="row">
            <div className="col-xl-12">
              <div className="settings-wrapper d-flex">
                <div className="settings-page-wrap">
                  <form onSubmit={handleSubmit}>
                    <div className="setting-title"  style={{display:"none"}}>
                      <h4>{businessProfile ? 'Edit Business Profile' : 'Create Business Profile'}</h4>
                    </div>
                    <div className="card-title-head">
                      <h6>
                        <span>
                          <User className="feather-chevron-up" />
                        </span>
                        Business Information
                      </h6>
                    </div>
                    <div className="profile-pic-upload">
                      <div className="profile-pic">
                        {selectedImage ? (
                          <img
                            src={imagePreview || selectedImage}
                            alt="Logo"
                            style={{
                              width: "150px",
                              height: "118px",
                              borderRadius: "10%",
                              objectFit: "cover"
                            }}
                            onClick={handleImageClick}
                          />
                        ) : (
                          <span>
                            <PlusCircle className="plus-down-add" />
                            Company Logo
                          </span>
                        )}
                      </div>
                      <div className="new-employee-field">
                        <div className="mb-0">
                          <div className="image-upload mb-0">
                            <input type="file" onChange={handleImageChange} accept="image/*" />
                            <div className="image-uploads">
                              <h4>Change Logo</h4>
                            </div>
                          </div>
                          <span>
                            For better preview recommended size is 450px x 450px. Max size 5MB.
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="row g-3">
                      <div className="col-lg-6 col-md-4">
                        <label className="form-label">Company Name</label>
                        <input
                          type="text"
                          className="form-control"
                          value={companyName}
                          onChange={(e) => setCompanyName(e.target.value)}
                          style={{ width: "100%", height: "38px" }}
                          required
                        />
                      </div>
                      <div className="col-lg-3 col-md-2">
                        <label className="form-label">Salary Period Start Date</label>
                        <input
                          type="number"
                          className="form-control"
                          placeholder="Start Date (1-31)"
                          value={salaryPeriod.startDate || ''}
                          onChange={(e) => setSalaryPeriod({ ...salaryPeriod, startDate: e.target.value })}
                          min="1"
                          max="31"
                          style={{ width: "100%", height: "38px" }}
                          required
                        />
                      </div>
                      <div className="col-lg-3 col-md-2">
                        <label className="form-label">Salary Period End Date</label>
                        <input
                          type="number"
                          className="form-control"
                          placeholder="End Date (1-31)"
                          value={salaryPeriod.endDate || ''}
                          onChange={(e) => setSalaryPeriod({ ...salaryPeriod, endDate: e.target.value })}
                          min="1"
                          max="31"
                          style={{ width: "100%", height: "38px" }}
                          required
                        />
                      </div>
                      <div className="col-lg-12">
                        <label className="form-label">Branches</label>
                        {branches.map((branch, index) => (
                          <div key={index} className="row g-3 mb-3">
                            <div className="col-lg-4 col-md-3">
                              <input
                                type="text"
                                className="form-control"
                                placeholder="Branch Name"
                                value={branch.branchName || ''}
                                onChange={(e) => handleBranchChange(index, 'branchName', e.target.value)}
                                style={{ width: "100%", height: "38px" }}
                                required
                              />
                            </div>
                            <div className="col-lg-3 col-md-2">
                              <input
                                type="text"
                                className="form-control"
                                placeholder="Street"
                                value={branch.address?.street || ''}
                                onChange={(e) => handleBranchChange(index, 'address', e.target.value, 'street')}
                                style={{ width: "100%", height: "38px" }}
                              />
                            </div>
                            <div className="col-lg-2 col-md-2">
                              <input
                                type="text"
                                className="form-control"
                                placeholder="City"
                                value={branch.address?.city || ''}
                                onChange={(e) => handleBranchChange(index, 'address', e.target.value, 'city')}
                                style={{ width: "100%", height: "38px" }}
                              />
                            </div>
                            <div className="col-lg-2 col-md-2">
                              <input
                                type="text"
                                className="form-control"
                                placeholder="State"
                                value={branch.address?.state || ''}
                                onChange={(e) => handleBranchChange(index, 'address', e.target.value, 'state')}
                                style={{ width: "100%", height: "38px" }}
                              />
                            </div>
                            <div className="col-lg-2 col-md-2">
                              <input
                                type="text"
                                className="form-control"
                                placeholder="Country"
                                value={branch.address?.country || ''}
                                onChange={(e) => handleBranchChange(index, 'address', e.target.value, 'country')}
                                style={{ width: "100%", height: "38px" }}
                              />
                            </div>
                            <div className="col-lg-2 col-md-2">
                              <input
                                type="text"
                                className="form-control"
                                placeholder="Postal Code"
                                value={branch.address?.postalCode || ''}
                                onChange={(e) => handleBranchChange(index, 'address', e.target.value, 'postalCode')}
                                style={{ width: "100%", height: "38px" }}
                              />
                            </div>
                            <div className="col-lg-3 col-md-2">
                              <input
                                type="text"
                                className="form-control"
                                placeholder="Phone"
                                value={branch.contact?.phone || ''}
                                onChange={(e) => handleBranchChange(index, 'contact', e.target.value, 'phone')}
                                style={{ width: "100%", height: "38px" }}
                              />
                            </div>
                            <div className="col-lg-3 col-md-2">
                              <input
                                type="email"
                                className="form-control"
                                placeholder="Email"
                                value={branch.contact?.email || ''}
                                onChange={(e) => handleBranchChange(index, 'contact', e.target.value, 'email')}
                                style={{ width: "100%", height: "38px" }}
                              />
                            </div>
                            <div className="col-lg-1 col-md-1">
                              <DeleteOutlined
                                style={{ color: 'red', cursor: 'pointer' }}
                                onClick={() => removeBranch(index)}
                              />
                            </div>
                          </div>
                        ))}
                        <Button type="dashed" onClick={addBranch} style={{ width: '100%' }}>
                          Add Branch
                        </Button>
                      </div>
                      <div className="col-lg-6 col-md-4">
                        <label className="form-label">Contact Phone</label>
                        <input
                          type="text"
                          className="form-control"
                          value={contact.phone || ''}
                          onChange={(e) => setContact({ ...contact, phone: e.target.value })}
                          style={{ width: "100%", height: "38px" }}
                        />
                      </div>
                      <div className="col-lg-6 col-md-4">
                        <label className="form-label">Contact Email</label>
                        <input
                          type="email"
                          className="form-control"
                          value={contact.email || ''}
                          onChange={(e) => setContact({ ...contact, email: e.target.value })}
                          style={{ width: "100%", height: "38px" }}
                        />
                      </div>
                      <div className="col-lg-3 col-md-2">
                        <label className="form-label">Street</label>
                        <input
                          type="text"
                          className="form-control"
                          value={address.street || ''}
                          onChange={(e) => setAddress({ ...address, street: e.target.value })}
                          style={{ width: "100%", height: "38px" }}
                        />
                      </div>
                      <div className="col-lg-3 col-md-2">
                        <label className="form-label">City</label>
                        <input
                          type="text"
                          className="form-control"
                          value={address.city || ''}
                          onChange={(e) => setAddress({ ...address, city: e.target.value })}
                          style={{ width: "100%", height: "38px" }}
                        />
                      </div>
                      <div className="col-lg-3 col-md-2">
                        <label className="form-label">State</label>
                        <input
                          type="text"
                          className="form-control"
                          value={address.state || ''}
                          onChange={(e) => setAddress({ ...address, state: e.target.value })}
                          style={{ width: "100%", height: "38px" }}
                        />
                      </div>
                      <div className="col-lg-3 col-md-2">
                        <label className="form-label">Country</label>
                        <input
                          type="text"
                          className="form-control"
                          value={address.country || ''}
                          onChange={(e) => setAddress({ ...address, country: e.target.value })}
                          style={{ width: "100%", height: "38px" }}
                        />
                      </div>
                      <div className="col-lg-3 col-md-2">
                        <label className="form-label">Postal Code</label>
                        <input
                          type="text"
                          className="form-control"
                          value={address.postalCode || ''}
                          onChange={(e) => setAddress({ ...address, postalCode: e.target.value })}
                          style={{ width: "100%", height: "38px" }}
                        />
                      </div>
                      <div className="col-lg-6 col-md-4">
                        <label className="form-label">Website</label>
                        <input
                          type="text"
                          className="form-control"
                          value={additionalDetails.website || ''}
                          onChange={(e) => setAdditionalDetails({ ...additionalDetails, website: e.target.value })}
                          style={{ width: "100%", height: "38px" }}
                        />
                      </div>
                      <div className="col-lg-6 col-md-4">
                        <label className="form-label">Industry</label>
                        <input
                          type="text"
                          className="form-control"
                          value={additionalDetails.industry || ''}
                          onChange={(e) => setAdditionalDetails({ ...additionalDetails, industry: e.target.value })}
                          style={{ width: "100%", height: "38px" }}
                        />
                      </div>
                      <div className="mb-0 d-flex justify-content-end">
                        <button type="button" className="btn btn-cancel me-2" onClick={handleCancel}>
                          Cancel
                        </button>
                        <button type="submit" className="btn btn-submit me-2" disabled={loading}>
                          {loading ? <Spin size="small" /> : businessProfile ? 'Update Profile' : 'Save Profile'}
                        </button>
                        {businessProfile && (
                          <button type="button" className="btn btn-danger" onClick={handleDelete}>
                            Delete Profile
                          </button>
                        )}
                      </div>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Modal show={showModal} onHide={handleCloseModal} size="md" centered>
        <div style={{ position: 'relative' }}>
          <CloseOutlined
            onClick={handleCloseModal}
            style={{
              position: 'absolute',
              top: 10,
              right: 10,
              fontSize: '16px',
              cursor: 'pointer',
              zIndex: 1,
              color: "red"
            }}
          />
          <Modal.Body style={{ padding: 0, margin: 0 }}>
            {selectedImage && (
              <img
                src={imagePreview || selectedImage}
                alt="Full Size Preview"
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            )}
          </Modal.Body>
        </div>
      </Modal>
    </div>
  );
};

export default BusinessSettings;